import { BetPrediction } from '@svenskaspelab/ui/react/bet-prediction';
import type { ReactNode } from 'react';

import { type CouponIndex } from '../../schema/coupon/entry.js';

export type CouponOutcomePredictionBaseProps = {
  buttonClassName?: string;
  isSelected?: boolean;
  // outcome is optional since it could be over/under with no outcome for X outcome
  outcome?: string | number;
  index: number;
  buttonSize?: React.ComponentProps<typeof BetPrediction>['size'];
  state?: React.ComponentProps<typeof BetPrediction>['state'];
  reducedPercentage?: React.ComponentProps<typeof BetPrediction>['reducedPercentage'];
};

export type CouponOutcomePredictionProps = CouponOutcomePredictionBaseProps & {
  eventNumber: number;
  couponIndex?: CouponIndex;
  status?: React.ComponentProps<typeof BetPrediction>['status'];
};

/**
 * This component renders the correct structure for an outcome in a coupon. It handles if the outcome is disabled and if it is selected.
 *
 * @param root0 The couponoutcome input props
 * @param root0.eventNumber Number for event
 * @param root0.index Index for the outcome
 * @param root0.isSelected wether or not the outcome is selected
 * @param root0.outcome Outcome to be displayed
 * @param root0.couponIndex Index of the coupon, used for multi coupon
 * @param root0.buttonSize Size of the ui button
 * @param root0.buttonClassName Classname for the button, used for styling
 * @param root0.status Status for the outcome, used for styling correct and incorrect outcomes
 * @param root0.state State for the outcome
 * @param root0.reducedPercentage Percentage to reduce the outcome by, used for styling the outcome distribution
 * @returns Component to visually represent an outcome and select it if needed
 */
export default function CouponOutcomePrediction({
  eventNumber,
  index,
  isSelected,
  outcome,
  couponIndex,
  buttonSize,
  buttonClassName,
  status,
  state,
  reducedPercentage,
}: CouponOutcomePredictionProps): ReactNode {
  return (
    <BetPrediction
      className={buttonClassName}
      data-testid={`couponButton_${eventNumber}_${index}${couponIndex === undefined ? '' : `_${couponIndex}`}`}
      size={buttonSize}
      value={outcome?.toString() ?? ''}
      state={state ?? (isSelected ? 'selected' : 'unselected')}
      status={status ?? 'default'}
      reducedPercentage={reducedPercentage}
    >
      {outcome}
    </BetPrediction>
  );
}
